import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import InputRadioGroup from 'common/components/Elements/Form/InputRadioGroup/InputRadioGroup';
import { deepClone } from 'common/utilities/core/object';
import I18n from 'i18n/en-US';
import React from 'react';
import isEqual from 'react-fast-compare';
import { SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import ColorsConservation from 'sequenceAnalyser/components/Colors/ColorsConservation/ColorsConservation';
import ColorsHydrophobicity from 'sequenceAnalyser/components/Colors/ColorsHydrophobicity/ColorsHydrophobicity';
import ColorsResidues from 'sequenceAnalyser/components/Colors/ColorsResidues/ColorsResidues';
import type {
ColorInfos, ColorScheme,
  SelectedColorScheme} from 'sequenceAnalyser/utilities/colors';
import { ColorSchemesName, getColorSchemes, getColorsResiduesGroups } from 'sequenceAnalyser/utilities/colors';
import { getSelectedColorSchemeFromColorScheme } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

interface ComponentProps {
  onColorSchemeChange: (scheme?: SelectedColorScheme) => void;
  colors?: SelectedColorScheme;
  sessionSequenceType: SequenceType;
}

interface ComponentState {
  colors?: SelectedColorScheme;
}

class Colors extends React.Component<ComponentProps, ComponentState> {
  public constructor(props: ComponentProps) {
    super(props);

    this.state = {
      colors: props.colors,
    };
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { colors } = this.state;

    if (!isEqual(colors, prevState.colors)) {
      this.handleUpdate();
    }
  }

  handleColorSchemeChange = (name: ColorSchemesName) => {
    const { sessionSequenceType } = this.props;

    this.setState((prevState) => {
      const newColors = name === ColorSchemesName.None
        ? undefined
        : getSelectedColorSchemeFromColorScheme(
          sessionSequenceType,
          name,
          getColorSchemes(sessionSequenceType)[name] as ColorScheme,
        );

      // When switching scheme, we want to keep the previous configuration
      if (newColors && prevState.colors) {
        const prevInfos = prevState.colors.infos;
        newColors.conservation = prevState.colors.conservation;
        newColors.topOccurrenceOnly = prevState.colors.topOccurrenceOnly;

        newColors.infos = Object.keys(newColors.infos).reduce<ColorInfos>((acc, letter) => {
          const newAcc: ColorInfos = { ...acc };
          newAcc[letter] = { ...newColors.infos[letter], show: prevInfos[letter].show };
          return newAcc;
        }, {});
      }

      return { colors: newColors };
    });
  };

  handleResidueVisibilityToggle = (residue: string) => {
    this.setState((prevState) => {
      const newColors: SelectedColorScheme | undefined = deepClone(prevState.colors);

      if (newColors) {
        newColors.infos[residue] = {
          ...newColors.infos[residue],
          show: !newColors.infos[residue].show,
        };
      }

      return { colors: newColors };
    });
  };

  handleResiduesVisibilityChange = (residues: string[], show: boolean) => {
    this.setState((prevState) => {
      const newColors: SelectedColorScheme | undefined = deepClone(prevState.colors);

      if (newColors) {
        residues.forEach((residue) => {
          newColors.infos[residue] = {
            ...newColors.infos[residue],
            show,
          };
        });
      }

      return { colors: newColors };
    });
  };

  handleUpdate = () => {
    const { onColorSchemeChange } = this.props;
    const { colors } = this.state;

    onColorSchemeChange(colors);
  };

  handleConservationChange = (conservation: number) => {
    this.setState((prevState) => {
      const newColors: SelectedColorScheme | undefined = deepClone(prevState.colors);

      if (newColors) {
        newColors.conservation = conservation;
      }

      return { colors: newColors };
    });
  };

  handleHydrophobicityChange = (hydrophobicity: SelectedColorScheme['hydrophobicity']) => {
    this.setState((prevState) => {
      const newColors: SelectedColorScheme | undefined = deepClone(prevState.colors);

      if (newColors) {
        newColors.hydrophobicity = hydrophobicity;
      }

      return { colors: newColors };
    });
  };

  toggleTopOccurrenceOnlyChange = () => {
    this.setState((prevState) => {
      const newColors: SelectedColorScheme | undefined = deepClone(prevState.colors);

      if (newColors) {
        newColors.topOccurrenceOnly = !newColors.topOccurrenceOnly;
      }

      return { colors: newColors };
    });
  };

  public render() {
    const { colors } = this.state;
    const { sessionSequenceType } = this.props;

    return (
      <Grid container pl={0.5} pr={0.5}>
        <Grid container>
          <Grid item xs={6}>
            <InputRadioGroup
              options={[
                ...(Object.keys(getColorSchemes(sessionSequenceType)) as ColorSchemesName[])
                  .filter((name) => name === ColorSchemesName.None)
                  .map((name) => ({ value: name, label: name })),
              ]}
              onChange={(value) => this.handleColorSchemeChange(value as ColorSchemesName)}
              value={colors ? colors.name.toString() : ColorSchemesName.None.toString()}
            />
            <InputRadioGroup
              label={I18n.SequenceAnalyser.Colors.schemes}
              options={[
                ...(Object.keys(getColorSchemes(sessionSequenceType)) as ColorSchemesName[])
                  .filter((name) => (
                    !(getColorSchemes(sessionSequenceType)[name])?.hydrophobicValues && name !== ColorSchemesName.None
                  ))
                  .map((name) => ({ value: name, label: name })),
              ]}
              onChange={(value) => this.handleColorSchemeChange(value as ColorSchemesName)}
              value={colors ? colors.name.toString() : ColorSchemesName.None.toString()}
              modalLabel
            />
            {sessionSequenceType === SequenceType.protein && (
              <InputRadioGroup
                label={I18n.SequenceAnalyser.Colors.hydrophobicity}
                options={[
                  ...(Object.keys(getColorSchemes(sessionSequenceType)) as ColorSchemesName[])
                    .filter((name) => (getColorSchemes(sessionSequenceType)[name])?.hydrophobicValues)
                    .map((name) => ({ value: name, label: name })),
                ]}
                onChange={(value) => this.handleColorSchemeChange(value as ColorSchemesName)}
                value={colors ? colors.name.toString() : ColorSchemesName.None.toString()}
                modalLabel
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {colors && sessionSequenceType === SequenceType.protein && (
              <>
                <ColorsConservation
                  colors={colors}
                  onConservationChange={this.handleConservationChange}
                />
                <Box sx={{ mt: -0.5 }}>
                  <InputCheckbox
                    checked={colors.topOccurrenceOnly}
                    label={I18n.SequenceAnalyser.Colors.topOccurrences}
                    onChange={() => this.toggleTopOccurrenceOnlyChange()}
                  />
                </Box>
                {colors.hydrophobicity && (
                  <ColorsHydrophobicity
                    key={colors.name}
                    colors={colors}
                    onHydrophobicityChange={this.handleHydrophobicityChange}
                  />
                )}
              </>
            )}
          </Grid>
        </Grid>
        {colors && (
          <Box mt={2}>
            <ColorsResidues
              colors={colors}
              onResidueVisibilityToggle={this.handleResidueVisibilityToggle}
              onResiduesVisibilityToggle={this.handleResiduesVisibilityChange}
              colorsResiduesGroups={getColorsResiduesGroups(sessionSequenceType)}
            />
          </Box>
        )}
      </Grid>
    );
  }
}

export default Colors;
